/**
 * ExportImport - Composant d'export/import des configurations RBAC
 */

import React, { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Download, Upload, FileJson, FileText, RefreshCw,
  CheckCircle, AlertTriangle, Shield, Copy, Check
} from 'lucide-react';
import axios from 'axios';
import { toast } from '../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ExportImport = ({ onRefresh }) => {
  const [exportFormat, setExportFormat] = useState('json');
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importData, setImportData] = useState('');
  const [mergeMode, setMergeMode] = useState('skip');
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const fileInputRef = useRef(null);

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/rbac/export/roles?format=${exportFormat}`);
      
      if (exportFormat === 'json') {
        // Télécharger en JSON
        const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `roles_export_${new Date().toISOString().slice(0, 10)}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        setPreviewData(response.data);
        
        toast({
          title: "Export réussi",
          description: `${response.data.roles?.length || 0} rôles exportés au format JSON`
        });
      } else {
        // CSV - la réponse est déjà le contenu CSV
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `roles_export_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        toast({
          title: "Export réussi",
          description: "Rôles exportés au format CSV"
        });
      }
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible d'exporter les rôles",
        variant: "destructive"
      });
    } finally {
      setExporting(false);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const content = event.target?.result;
        setImportData(content);
        
        // Prévisualiser le contenu
        const parsed = JSON.parse(content);
        setPreviewData(parsed);
        setShowImportDialog(true);
      } catch (error) {
        toast({
          title: "Erreur",
          description: "Fichier JSON invalide",
          variant: "destructive"
        });
      }
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (!importData) {
      toast({
        title: "Erreur",
        description: "Veuillez fournir des données à importer",
        variant: "destructive"
      });
      return;
    }

    setImporting(true);
    try {
      const data = typeof importData === 'string' ? JSON.parse(importData) : importData;
      
      const response = await axios.post(`${API}/rbac/import/roles?merge_mode=${mergeMode}`, data);
      
      setImportResult(response.data);
      
      toast({
        title: "Import réussi",
        description: `${response.data.created} créés, ${response.data.updated} mis à jour, ${response.data.skipped} ignorés`
      });
      
      if (onRefresh) onRefresh();
      
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Erreur lors de l'import",
        variant: "destructive"
      });
    } finally {
      setImporting(false);
    }
  };

  const copyToClipboard = () => {
    if (previewData) {
      navigator.clipboard.writeText(JSON.stringify(previewData, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast({ title: "Copié dans le presse-papiers" });
    }
  };

  const resetImport = () => {
    setImportData('');
    setPreviewData(null);
    setImportResult(null);
    setShowImportDialog(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="space-y-6">
      {/* Export Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Download className="w-5 h-5 text-emerald-600" />
            Exporter la configuration
          </CardTitle>
          <CardDescription>
            Exportez tous les rôles et leurs permissions pour sauvegarde ou transfert
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Format d&apos;export</Label>
              <Select value={exportFormat} onValueChange={setExportFormat}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="json">
                    <div className="flex items-center gap-2">
                      <FileJson className="w-4 h-4" />
                      JSON (complet)
                    </div>
                  </SelectItem>
                  <SelectItem value="csv">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      CSV (simplifié)
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button
                onClick={handleExport}
                disabled={exporting}
                className="w-full bg-emerald-600 hover:bg-emerald-700"
              >
                {exporting ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                Exporter
              </Button>
            </div>
          </div>

          {/* Preview */}
          {previewData && exportFormat === 'json' && (
            <div className="p-4 bg-gray-50 rounded-lg border">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Aperçu de l&apos;export</span>
                <Button variant="ghost" size="sm" onClick={copyToClipboard}>
                  {copied ? (
                    <Check className="w-4 h-4 text-emerald-600" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
              </div>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Rôles</p>
                  <p className="font-semibold">{previewData.roles?.length || 0}</p>
                </div>
                <div>
                  <p className="text-gray-500">Permissions</p>
                  <p className="font-semibold">{previewData.permissionsCatalog?.length || 0}</p>
                </div>
                <div>
                  <p className="text-gray-500">Catégories</p>
                  <p className="font-semibold">{previewData.permissionCategories?.length || 0}</p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Import Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Upload className="w-5 h-5 text-blue-600" />
            Importer une configuration
          </CardTitle>
          <CardDescription>
            Importez des rôles depuis un fichier JSON exporté précédemment
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Fichier JSON</Label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                onChange={handleFileUpload}
                className="block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-md file:border-0
                  file:text-sm file:font-semibold
                  file:bg-emerald-50 file:text-emerald-700
                  hover:file:bg-emerald-100
                  cursor-pointer"
              />
            </div>
            <div className="space-y-2">
              <Label>Mode de fusion</Label>
              <Select value={mergeMode} onValueChange={setMergeMode}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="skip">
                    Ignorer les existants
                  </SelectItem>
                  <SelectItem value="override">
                    Remplacer les existants
                  </SelectItem>
                  <SelectItem value="merge">
                    Fusionner les permissions
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
            <p className="font-medium mb-1">Modes de fusion :</p>
            <ul className="list-disc list-inside space-y-1 text-xs">
              <li><strong>Ignorer :</strong> Les rôles existants ne seront pas modifiés</li>
              <li><strong>Remplacer :</strong> Les rôles existants seront remplacés par les nouvelles valeurs</li>
              <li><strong>Fusionner :</strong> Les permissions seront combinées avec celles existantes</li>
            </ul>
          </div>

          {/* Import manually with JSON */}
          <div className="space-y-2">
            <Label>Ou coller directement le JSON</Label>
            <Textarea
              placeholder='{"roles": [...], "permissionsCatalog": [...]}'
              value={importData}
              onChange={(e) => setImportData(e.target.value)}
              rows={4}
              className="font-mono text-xs"
            />
          </div>

          <Button
            onClick={() => {
              if (importData) {
                try {
                  setPreviewData(JSON.parse(importData));
                  setShowImportDialog(true);
                } catch {
                  toast({
                    title: "Erreur",
                    description: "JSON invalide",
                    variant: "destructive"
                  });
                }
              }
            }}
            disabled={!importData}
            className="w-full"
            variant="outline"
          >
            <Upload className="w-4 h-4 mr-2" />
            Prévisualiser et importer
          </Button>
        </CardContent>
      </Card>

      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Confirmer l&apos;import</DialogTitle>
            <DialogDescription>
              Vérifiez les données avant d&apos;importer
            </DialogDescription>
          </DialogHeader>

          {!importResult ? (
            <div className="space-y-4 py-4">
              {previewData && (
                <div className="space-y-3">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      Rôles à importer
                    </h4>
                    <div className="space-y-2">
                      {previewData.roles?.slice(0, 5).map((role, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm">
                          <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: role.color || '#6366f1' }}
                          />
                          <span>{role.name}</span>
                          <Badge variant="outline" className="text-xs">
                            {role.permissions?.length || 0} perms
                          </Badge>
                        </div>
                      ))}
                      {previewData.roles?.length > 5 && (
                        <p className="text-xs text-gray-500">
                          ... et {previewData.roles.length - 5} autres
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 text-amber-700 bg-amber-50 p-3 rounded-lg">
                    <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                    <div className="text-sm">
                      Mode sélectionné : <strong>{
                        mergeMode === 'skip' ? 'Ignorer les existants' :
                        mergeMode === 'override' ? 'Remplacer les existants' :
                        'Fusionner les permissions'
                      }</strong>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-emerald-50 rounded-lg">
                <div className="flex items-center gap-2 text-emerald-700 mb-3">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-medium">Import terminé</span>
                </div>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Créés</p>
                    <p className="font-semibold text-emerald-600">{importResult.created}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Mis à jour</p>
                    <p className="font-semibold text-blue-600">{importResult.updated}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Ignorés</p>
                    <p className="font-semibold text-gray-600">{importResult.skipped}</p>
                  </div>
                </div>
              </div>

              {importResult.errors?.length > 0 && (
                <div className="p-4 bg-red-50 rounded-lg">
                  <p className="font-medium text-red-700 mb-2">Erreurs :</p>
                  <ul className="text-sm text-red-600 space-y-1">
                    {importResult.errors.map((err, idx) => (
                      <li key={idx}>• {err.role}: {err.error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            {!importResult ? (
              <>
                <Button variant="outline" onClick={resetImport}>
                  Annuler
                </Button>
                <Button
                  onClick={handleImport}
                  disabled={importing}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {importing ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  Confirmer l&apos;import
                </Button>
              </>
            ) : (
              <Button onClick={resetImport}>
                Fermer
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExportImport;
